import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Target, HelpCircle, CheckCircle2, XCircle } from "lucide-react";
import {
  useTargetConnection,
  useUpdateTargetConnection,
  useUpdateTargetCredentials,
  useTestTargetConnection,
} from "@/hooks/useApi";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { GrowSignalsPanel } from "@/components/projects/GrowSignalsPanel";
import { AdtSessionPanel } from "@/components/projects/AdtSessionPanel";
import { CbcConnectionTab } from "@/components/projects/CbcConnectionTab";
import { ConfigurationTenantForm } from "@/components/projects/ConfigurationTenantForm";

export type TargetSheetTab = "connection" | "adt" | "cbc" | "signals";
export type TargetSheetSubTab = "development" | "configuration";

interface TargetConnectionSheetProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Aba em que o modal abre. Quem abre decide só isto; dentro do modal a pessoa troca à vontade. */
  initialTab?: TargetSheetTab;
  /** Sub-aba de Connection em que o modal abre. */
  initialSubTab?: TargetSheetSubTab;
}

/** Modal único das conexões do ambiente alvo (GROW): cadastra e edita Development,
 * Configuration, CBC, sessão ADT e sinais. É controlado de fora — o card de resumo
 * (Overview/legado) e a seção de três cards de /connections abrem o mesmo modal. */
export function TargetConnectionSheet({
  projectId,
  open,
  onOpenChange,
  initialTab = "connection",
  initialSubTab = "development",
}: TargetConnectionSheetProps) {
  const { t } = useTranslation();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Target className="w-4 h-4" /> {t("GROW Connection")}
          </SheetTitle>
          <SheetDescription>
            {t(
              "The target GROW environment for this project. Required for the Code Companion (deploy in Realize); optional for the Architect, where it enriches the analysis but is not a prerequisite.",
            )}
          </SheetDescription>
        </SheetHeader>
        <SheetBody projectId={projectId} initialTab={initialTab} initialSubTab={initialSubTab} />
      </SheetContent>
    </Sheet>
  );
}

/** Corpo do modal. Só existe com o Sheet aberto (o conteúdo desmonta ao fechar), então cada
 * abertura começa da aba pedida e com o estado do formulário relido do que está gravado. */
function SheetBody({
  projectId,
  initialTab,
  initialSubTab,
}: {
  projectId: string;
  initialTab: TargetSheetTab;
  initialSubTab: TargetSheetSubTab;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data } = useTargetConnection(projectId, "development");
  const update = useUpdateTargetConnection(projectId, "development");
  const saveCreds = useUpdateTargetCredentials(projectId);
  const test = useTestTargetConnection(projectId, "development");

  const hasCreds = !!data?.has_credentials;

  const [baseUrl, setBaseUrl] = useState("");
  const [release, setRelease] = useState("");
  const [client, setClient] = useState("");
  const [softwareComponent, setSoftwareComponent] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [servicePath, setServicePath] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBaseUrl(data?.base_url ?? "");
    setRelease(data?.release ?? "");
    setClient(data?.client ?? "");
    setSoftwareComponent(data?.software_component ?? "");
    // Reexibe os campos NÃO-secretos das credenciais (a senha nunca volta).
    setUsername(data?.credentials?.username ?? "");
    setServicePath(data?.credentials?.service_path ?? "");
  }, [
    data?.base_url,
    data?.release,
    data?.client,
    data?.software_component,
    data?.credentials?.username,
    data?.credentials?.service_path,
  ]);

  // Uma única ação: grava a identidade sempre; grava as credenciais quando a
  // senha foi (re)digitada nesta sessão (a senha nunca volta preenchida).
  const handleSave = async () => {
    setSaving(true);
    try {
      await update.mutateAsync({
        target_type: "grow_public",
        base_url: baseUrl.trim() || undefined,
        release: release.trim() || undefined,
        client: client.trim() || undefined,
        software_component: softwareComponent.trim() || undefined,
        client_role: "development",
      });
      if (password.trim() && username.trim() && servicePath.trim()) {
        await saveCreds.mutateAsync({
          auth_type: "basic",
          service_path: servicePath.trim(),
          username: username.trim(),
          password,
        });
        setPassword("");
      }
      toast({ title: t("Target connection saved") });
    } catch {
      toast({ title: t("Could not save target connection"), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
        <Tabs defaultValue={initialTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="connection">{t("Connection")}</TabsTrigger>
            <TabsTrigger value="adt">{t("My ADT session")}</TabsTrigger>
            <TabsTrigger value="cbc">{t("CBC")}</TabsTrigger>
            <TabsTrigger value="signals">{t("Signals")}</TabsTrigger>
          </TabsList>

          <TabsContent value="connection" className="mt-4">
            {/* Qual conexão se edita: o seletor vem antes do formulário, que é longo. */}
            <Tabs defaultValue={initialSubTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="development">{t("Development")}</TabsTrigger>
                <TabsTrigger value="configuration">{t("Configuration")}</TabsTrigger>
              </TabsList>

              <TabsContent value="development" className="mt-4 space-y-4">
            <FirstConnectionGuide />
          {/* Identidade */}
          <div className="space-y-1.5">
            <Label>{t("Target type")}</Label>
            <Input value="SAP S/4HANA Cloud Public (GROW)" disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="target-base-url">{t("Base URL")}</Label>
            <Input
              id="target-base-url"
              placeholder="https://myNNNNNN-api.s4hana.cloud.sap"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="target-release">{t("Release")}</Label>
              <Input id="target-release" placeholder="2508" value={release} onChange={(e) => setRelease(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="target-client">{t("Client")}</Label>
              <Input id="target-client" placeholder="080" value={client} onChange={(e) => setClient(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="target-swc">{t("Software component")}</Label>
            <Input
              id="target-swc"
              placeholder="ZPARTNER"
              value={softwareComponent}
              onChange={(e) => setSoftwareComponent(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("Transportable SWC at the top of the target package hierarchy. Used to generate deployable manifests and create the package chain.")}
            </p>
          </div>

          {/* Credenciais OData */}
          <div className="pt-4 border-t">
            <h4 className="text-sm font-semibold text-[#08254c]">{t("OData credentials")}</h4>
            <p className="text-xs text-muted-foreground mb-3">
              {t("Optional now: needed for the live test and for the Architect to read the target. Stored encrypted; the password is never shown again.")}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="target-user">{t("Communication user")}</Label>
                <Input id="target-user" placeholder="MYGROW_API" value={username} onChange={(e) => setUsername(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="target-pass">{t("Password")}</Label>
                <Input id="target-pass" type="password" placeholder={hasCreds ? "••••••••" : ""} value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5 mt-4">
              <Label htmlFor="target-svc">{t("Service URL")}</Label>
              <Input
                id="target-svc"
                placeholder="/sap/opu/odata4/sap/.../0001/"
                value={servicePath}
                onChange={(e) => setServicePath(e.target.value)}
              />
            </div>
          </div>

          {/* Ações: um Save + Testar */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t("Save")}
            </Button>
            <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !hasCreds}>
              {test.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t("Test connection")}
            </Button>
          </div>
          {test.data && (
            <div
              className={
                "flex items-center gap-2 text-sm " +
                (test.data.success ? "text-success" : "text-destructive")
              }
            >
              {test.data.success ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              <span>{test.data.message}</span>
            </div>
          )}
              </TabsContent>

              <TabsContent value="configuration" className="mt-4">
                <ConfigurationTenantForm projectId={projectId} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="adt" className="mt-4">
            <AdtSessionPanel projectId={projectId} />
          </TabsContent>

          <TabsContent value="cbc" className="mt-4">
            <CbcConnectionTab projectId={projectId} />
          </TabsContent>

          <TabsContent value="signals" className="mt-4">
            <GrowSignalsPanel projectId={projectId} />
          </TabsContent>
        </Tabs>
  );
}

/** Modal com o passo-a-passo detalhado de primeira conexão (setup no GROW). */
function FirstConnectionGuide() {
  const { t } = useTranslation();
  const steps = [
    {
      title: t("Step 1: Communication user"),
      body: t("In the GROW launchpad, open Maintain Communication Users and click New. Set a User Name (e.g. MYGROW_API), click Propose Password and save that password now (it is shown only once). Then Save."),
    },
    {
      title: t("Step 2: Communication system"),
      body: t("Open Communication Systems, click New (e.g. MYGROW_INT) and Create. Scroll to Users for Inbound Communication, add the user from step 1 with Authentication Method set to User ID and Password, then Save."),
    },
    {
      title: t("Step 3: Communication arrangement"),
      body: t("Open Communication Arrangements, click New and pick an available OData scenario (e.g. SAP_COM_0A05). Select the system from step 2, copy the Inbound Service URL, and Save so it becomes Active."),
    },
    {
      title: t("Step 4: Fill it in here"),
      body: t("Back on this card: fill Base URL (the API host, like https://myNNNNNN-api.s4hana.cloud.sap), Release and Client; then the communication user, its password and the Service URL. Click Save, then Test connection."),
    },
  ];
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#08254c] hover:text-[#b57a00]"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          {t("First connection guide")}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("First connection guide")}</DialogTitle>
          <DialogDescription>
            {t("myGROW reads your GROW system through an OData communication user. Set it up once in the SAP launchpad, then fill the fields on this card.")}
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-4">
          {steps.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-[#08254c] text-white text-xs font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              <div>
                <div className="text-sm font-semibold text-[#08254c]">{s.title}</div>
                <p className="text-sm text-muted-foreground mt-0.5">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
}
